import { h, render, type VNode } from "vue";
import VToast from "./VToast.vue";
import type { IPositionToast, IToastProps } from "./VToast.types";

export interface createManagerOptions {
  maxItemsCount: number;
  screenOffset: string;
  itemsGap?: string;
}
export type ToastSet = {
  container: {
    __id: string;
    id: string;
  };
  items: Map<number, VNode>;
};

export type ToastOptions = IToastProps & {
  onClickButton?: () => void;
  onClose?: () => void;
  onDestroy?: () => void;
};

export const createManager = (
  managerOptions: createManagerOptions = {
    maxItemsCount: 1,
    screenOffset: "20px",
  },
) => {
  let seed = 1;
  const { screenOffset, maxItemsCount, itemsGap } = managerOptions;

  const createContainer = (position: IPositionToast) => {
    const id = `toast_manager_container_${position}`;

    const tblr = {
      top: { top: screenOffset, right: "50%" },
      bottom: { bottom: screenOffset, right: "50%" },
    };
    const vNode = h("div", {
      id,
      style: {
        position: "fixed",
        ...tblr[position],
        transform: "translateX(50%)",
        "z-index": "var(--pl-z-index-toast)",
      },
    });
    const tempContainer = document.createElement("div");
    if (tempContainer) {
      render(vNode, tempContainer);
      if (tempContainer.firstElementChild) {
        document.body.appendChild(tempContainer.firstElementChild);
      }
    }

    return id;
  };

  const toasts: Record<IPositionToast, ToastSet> = (
    ["top", "bottom"] as IPositionToast[]
  ).reduce(
    (acc, position) => {
      acc[position] = {
        container: {
          __id: "",
          get id() {
            if (!this.__id) {
              this.__id = createContainer(position);
            }
            return this.__id;
          },
        },
        items: new Map(),
      };
      return acc;
    },
    {} as Record<IPositionToast, ToastSet>,
  );

  const open = (options: ToastOptions) => {
    const position = options.position || "top";
    const vmKey = seed++;
    const orientedToasts = toasts[position as IPositionToast];
    const container = document.getElementById(orientedToasts.container.id);

    if (orientedToasts.items.size >= maxItemsCount) {
      const oldestKey = [...orientedToasts.items.keys()].sort(
        (a, b) => a - b,
      )[0];

      orientedToasts.items.get(oldestKey)?.component?.emit("destroy");
    }

    if (container) {
      const tempContainer = document.createElement("div");
      if (tempContainer) {
        const vm = h(VToast, {
          key: vmKey,
          appear: true,
          gap: itemsGap,
          onDestroy: () => {
            render(null, tempContainer);
            orientedToasts.items.delete(vmKey);
          },
          ...(options as typeof VToast.props),
        });
        render(vm, tempContainer);
        orientedToasts.items.set(vmKey, vm);
        const domNode = tempContainer.firstChild;
        if (domNode) {
          container.appendChild(domNode);
        }
      }
    }
  };

  return {
    open,
    success: (options: ToastOptions) => open({ ...options, theme: "success" }),
    error: (options: ToastOptions) => open({ ...options, theme: "error" }),
    info: (options: ToastOptions) => open({ ...options, theme: "info" }),
    warning: (options: ToastOptions) => open({ ...options, theme: "warning" }),
    question: (options: ToastOptions) =>
      open({ ...options, theme: "question" }),
  };
};

let managerInstance: null | ReturnType<typeof createManager>;

export const getToastManagerInstance = () => {
  if (!managerInstance) managerInstance = createManager();
  return managerInstance;
};
